import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Card,
  IconButton,
  CardContent,
  Grid,
  Skeleton,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "./firebase";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  AreaChart,
  Area,
  XAxis,
  YAxis,
} from "recharts";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { VideoPlayer } from "./HelpDialogItems";
import { InstructionItem } from "./functions";

// Icons
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import QuizIcon from "@mui/icons-material/Quiz";

import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";

import StyleIcon from "@mui/icons-material/Style";
import LockIcon from "@mui/icons-material/Lock";
import RefreshIcon from "@mui/icons-material/Refresh";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SpeedIcon from "@mui/icons-material/Speed";
import DiamondIcon from "@mui/icons-material/Diamond";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import NewReleasesIcon from "@mui/icons-material/NewReleases";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import PodcastsIcon from "@mui/icons-material/Podcasts";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";

const ExplorationProgress = ({ userId, styles, theme }) => {
  const [loading, setLoading] = useState(true);
  const [dailyUsage, setDailyUsage] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const navigate = useNavigate();

  // Dynamic dialog content based on lesson ID

  const getDialogContent = (lessonId) => {
    switch (lessonId) {
      case "lessonShared":
        return {
          title: "Dela lektion med elever",
          description: "",
          createButtonText: "Generera lektion",
          createPath: "/app/lektioner",
          videoUrl:
            "https://sportkuriren.se/wp-content/uploads/share-lesson-2.mp4",
          poster:
            "https://sportkuriren.se/wp-content/uploads/share-lesson-2.png",
          // Add list items for this lesson type
          listItems: [
            { text: "Generera valfri lektion" },
            { text: "Klicka på 'Dela med elever' när lektionen är klar" },
            {
              text: "Välj ett elevrum och dela till alla eller enskilda elever",
            },
            { text: "Elever får en avisering och kan gå in på lektionen" },
            {
              text: "Du kan även dela lektionen via en länk och då krävs inget elevkonto.",
            },
          ],
        };
      case "quizShared":
        return {
          title: "Dela quiz med elever",
          description: "",
          createButtonText: "Skapa quiz",
          createPath: "/app/quiz",
          videoUrl: "https://sportkuriren.se/wp-content/uploads/share-quiz.mp4",
          posterUrl:
            "https://sportkuriren.se/wp-content/uploads/share-quiz.jpg",
          // Add list items for this lesson type
          listItems: [
            { text: "Generera ett quiz" },
            { text: "Gör eventuella redigeringar och spara" },
            { text: "Klicka på 'Dela med elever'" },
            {
              text: "Välj ett elevrum och dela till alla eller enskilda elever",
            },
            { text: "Se elevernas svar i realtid" },
          ],
        };
      case "flashcardsShared":
        return {
          title: "Dela flashcards med elever",
          description: "",
          createButtonText: "Skapa flashcards",
          createPath: "/app/flashcards/",
          videoUrl:
            "https://sportkuriren.se/wp-content/uploads/share-flashcards.mp4",
          posterUrl:
            "https://sportkuriren.se/wp-content/uploads/share-flashcards.jpg",
          // Add list items for this lesson type
          listItems: [
            { text: "Generera flashcards" },
            { text: "Gör eventuella redigeringar och spara" },
            { text: "Klicka på 'Dela med elever'" },
            {
              text: "Välj ett elevrum och dela till alla eller enskilda elever",
            },
          ],
        };
      case "chatbotShared":
        return {
          title: "Dela chatbot med elever",
          description: "",
          createButtonText: "Skapa chatbot",
          createPath: "/app/larare",
          videoUrl:
            "https://sportkuriren.se/wp-content/uploads/share-assistent.mp4",
          posterUrl:
            "https://sportkuriren.se/wp-content/uploads/share-assistent.jpg",
          // Add list items for this lesson type
          listItems: [
            { text: "Skapa en chatbot" },
            { text: "Instruera hur den ska bete sig" },
            { text: "Klicka på 'Dela med elever'" },
            {
              text: "Välj ett elevrum och dela till alla eller enskilda elever",
            },
          ],
        };
      default:
        return {
          title: "Dela med elever",
          description:
            "För att dela med elever behöver du skapa innehåll och sedan publicera det till ett elevrum.",
          createButtonText: "Skapa innehåll",
          createPath: "/dashboard",
          videoUrl:
            "https://storage.googleapis.com/studera-videos/lessons/dela-default.mp4",
          posterUrl:
            "https://storage.googleapis.com/studera-images/posters/dela-default-poster.jpg",
          // Default list items
          listItems: [
            { text: "Skapa ett elevrum om du inte redan har ett" },
            { text: "Generera innehåll med hjälp av AI" },
            { text: "Publicera till ditt elevrum" },
            { text: "Bjud in elever med delningslänk" },
          ],
        };
    }
  };

  // onLessonSelect function to add to your component
  const onLessonSelect = (lessonId) => {
    // Array of sharing options that should open a dialog
    const sharingOptions = [
      "lessonShared",
      "quizShared",
      "flashcardsShared",
      "chatbotShared",
    ];

    // Check if this is a sharing option
    if (sharingOptions.includes(lessonId)) {
      // Find the lesson details to display in dialog
      const lessonDetails = lessonTypes.find(
        (lesson) => lesson.id === lessonId,
      );
      setSelectedLesson({
        ...lessonDetails,
        dialogContent: getDialogContent(lessonId),
      });
      setDialogOpen(true);
    } else {
      // Regular options - navigate to appropriate path
      const routeMap = {
        lesson: "/app/lektioner",
        lessonPlan: "/app/lektioner/lektionsplan",
        powerpoint: "/app/generera-powerpoint",
        quiz: "/app/quiz",
        flashcard: "/app/flashcards",
        podcast: "/app/podcast",
        essay: "/app/uppsats/ny",
        rubric: "/app/matris/ny",
        imageAnalysis: "/assessment/image",
        assistent: "/app/larare",
        assistentTrained: "/app/larare",
        room: "/app/rum",
      };

      navigate(routeMap[lessonId] || "/dashboard");
    }
  };

  const [completed, setCompleted] = useState({
    quiz: false,
    flashcard: false,
    essay: false,
    podcast: false,
    rubric: false,
    lesson: false,
    lessonPlan: false,
    imageAnalysis: false,
    assistent: false,
    assistentTrained: false,
    room: false,
    powerpoint: false,
    quizShared: false,
  });

  // Group definitions with order and titles
  const lessonGroups = [
    {
      id: "create",
      title: "Skapa innehåll",
      description: "Skapa lektioner och material för dina elever",
      order: 1,
    },
    {
      id: "assessment",
      title: "Bedömning",
      description: "Verktyg för att bedöma och analysera elevarbeten",
      order: 2,
    },
    {
      id: "assistents",
      title: "Assistenter & Chatbotar",
      description: "Dela innehåll och interagera med dina elever",
      order: 3,
    },
    {
      id: "share",
      title: "Dela med elever",
      description: "Dela innehåll och interagera med dina elever",
      order: 4,
    },
  ];

  // Reorganize the lesson types into groups
  const lessonTypes = [
    // CREATE GROUP
    {
      id: "lesson",
      name: "Generera en lektion",
      description: "Välj bland 25 olika lektionstyper",
      icon: PodcastsIcon,
      color: "#8b5cf6",
      benefit: "Generera en lektion",
      action: "Prova Podcasts",
      benefitIcon: DiamondIcon,
      group: "create",
      order: 1,
    },
    {
      id: "lessonPlan",
      name: "Generera lektionsplan",
      description: "Välj ämne, lektionstid & och skriv ut",
      icon: PodcastsIcon,
      color: "#8b5cf6",
      benefit: "Generera en lektionsplanering",
      action: "Prova Podcasts",
      benefitIcon: DiamondIcon,
      group: "create",
      order: 2,
    },
    {
      id: "powerpoint",
      name: "Generera PowerPoint",
      description: "Låt AI skapa en PowerPoint",
      icon: PodcastsIcon,
      color: "#8b5cf6",
      benefit: "Generera en PowerPoint",
      action: "Prova Podcasts",
      benefitIcon: DiamondIcon,
      group: "create",
      order: 3,
    },
    {
      id: "quiz",
      name: "Generera quiz",
      description: "Generera quiz, skriv ut/dela",
      icon: QuizIcon,
      color: "#2ba658",
      benefit: "Generera quiz, skriv ut & dela",
      action: "Skapa din första frågesport",
      benefitIcon: SpeedIcon,
      group: "create",
      order: 4,
    },
    {
      id: "flashcard",
      name: "Generera flashcards",
      description: "Förbättra minnet med aktiv inlärning",
      icon: StyleIcon,
      color: "#f97316",
      benefit: "Generera quiz, skriv ut & dela",
      action: "Generera dina första flashkort",
      benefitIcon: LightbulbIcon,
      group: "create",
      order: 5,
    },
    {
      id: "podcast",
      name: "Generera en podcast",
      description: "Basera podden på valfritt material",
      icon: PodcastsIcon,
      color: "#8b5cf6",
      benefit: "Generera en podcast",
      action: "Prova Podcasts",
      benefitIcon: DiamondIcon,
      group: "create",
      order: 6,
    },
    // ASSISTENTS GROUP
    {
      id: "assistent",
      name: "Skapa en chatbot",
      description: "Skapa en helt egen chatbot",
      icon: PodcastsIcon,
      color: "#8b5cf6",
      benefit: "Generera en bedömningsmatris",
      action: "Prova Podcasts",
      benefitIcon: DiamondIcon,
      group: "assistents",
      order: 1,
    },

    {
      id: "assistentTrained",
      name: "Träna en assistent",
      description: "Träna en AI på valfritt dokument",
      icon: PodcastsIcon,
      color: "#8b5cf6",
      benefit: "Generera en bedömningsmatris",
      action: "Prova Podcasts",
      benefitIcon: DiamondIcon,
      group: "assistents",
      order: 2,
    },
    // ASSESSMENT GROUP
    {
      id: "essay",
      name: "Bedöm en text",
      description: "Låt AI betygsätta elevtexter",
      icon: AssignmentIcon,
      color: "#3b82f6",
      benefit: "Bedöm en text",
      action: "Prova bedömning",
      benefitIcon: DiamondIcon,
      group: "assessment",
      order: 1,
    },

    {
      id: "rubric",
      name: "Generera bedömningsmatris",
      description: "Låt AI skapa en bedömningsmatris",
      icon: PodcastsIcon,
      color: "#8b5cf6",
      benefit: "Generera en bedömningsmatris",
      action: "Prova Podcasts",
      benefitIcon: DiamondIcon,
      group: "assessment",
      order: 2,
    },

    // {
    //   id: "imageAnalysis",
    //   name: "Analysera en bild",
    //   description: "Ladda upp en bild och få en analys",
    //   icon: PodcastsIcon,
    //   color: "#8b5cf6",
    //   benefit: "Generera en bedömningsmatris",
    //   action: "Prova Podcasts",
    //   benefitIcon: DiamondIcon,
    //   group: "assessment",
    //   order: 2,
    // },

    // SHARE GROUP
    {
      id: "room",
      name: "Skapa elevrum",
      description: "Börja dela innehåll med elever",
      icon: PodcastsIcon,
      color: "#8b5cf6",
      benefit: "Aktivera för elever",
      action: "Prova Podcasts",
      benefitIcon: DiamondIcon,
      group: "share",
      order: 1,
    },
    {
      id: "lessonShared",
      name: "Dela lektion med elever",
      description: "Publicera lektioner till ditt elevrum",
      icon: PodcastsIcon,
      color: "#8b5cf6",
      group: "share",
      order: 2,
    },
    {
      id: "quizShared",
      name: "Dela quiz med elever",
      description: "Publicera quiz och se elevernas resultat",
      icon: PodcastsIcon,
      color: "#8b5cf6",
      group: "share",
      order: 3,
    },
    {
      id: "flashcardsShared",
      name: "Dela flashcards med elever",
      description: "Låt elever öva med dina flashcards",
      icon: PodcastsIcon,
      color: "#8b5cf6",
      group: "share",
      order: 4,
    },
    {
      id: "chatbotShared",
      name: "Dela chatbot med elever",
      description: "Låt elever använda din anpassade chatbot",
      icon: PodcastsIcon,
      color: "#8b5cf6",
      group: "share",
      order: 5,
    },
  ];
  // Fetch the user's lesson history
  useEffect(() => {
    const fetchLessonHistory = async () => {
      if (!userId) return;

      try {
        setLoading(true);
        const now = new Date();
        const endOfDay = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
        );
        endOfDay.setDate(endOfDay.getDate() + 1);

        const startDate = new Date(endOfDay);
        startDate.setDate(startDate.getDate() - 90);

        const lessonQuery = query(
          collection(db, "lesson_usage"),
          where("userId", "==", userId),
          where("timestamp", ">=", startDate),
          where("timestamp", "<", endOfDay),
        );
        const lessonDocs = await getDocs(lessonQuery);
        const userProgress = {
          quiz: false,
          flashcard: false,
          essay: false,
          podcast: false,
          rubric: false,
          lesson: false,
          lessonPlan: false,
          imageAnalysis: false,
          assistent: false,
          room: false,
          powerpoint: false,
        };

        lessonDocs.forEach((doc) => {
          const data = doc.data();
          if (data.lessonType === "QuizView.js") userProgress.quiz = true;
          if (data.lessonType === "Flashcards.js")
            userProgress.flashcard = true;
          if (data.lessonType === "EssayUpload.js") userProgress.essay = true;
          if (data.lessonType === "Podcast.js") userProgress.podcast = true;
          if (data.lessonType === "BasicReadingCompLesson.js")
            userProgress.lesson = true;
          if (data.lessonType === "LessonPlanRevamp.js")
            userProgress.lessonPlan = true;
          if (data.lessonType === "GenerateRubric.js")
            userProgress.rubric = true;
          if (data.lessonType === "VisionAnalysis.js")
            userProgress.imageAnalysis = true;
          if (data.lessonType === "RoomCreate.js") userProgress.room = true;
          if (data.lessonType === "PowerPointGenerator.js")
            userProgress.powerpoint = true;
          if (data.lessonType === "TeacherHandlerTrained.js")
            userProgress.assistentTrained = true;
          if (data.lessonType === "TeacherHandler.js")
            userProgress.assistent = true;

          if (data.lessonType === "QuizViewShared.js")
            userProgress.quizShared = true;

          if (data.lessonType === "FlashcardsShared.js")
            userProgress.flascardsShared = true;

          if (data.lessonType === "ChatbotShared.js")
            userProgress.chatbotShared = true;
        });

        // Prepare daily usage map
        const usageMap = {};

        // Initialize last 30 days
        for (let i = 0; i < 30; i++) {
          const date = new Date(startDate);
          date.setDate(date.getDate() + i);
          const key = date.toISOString().split("T")[0]; // 'YYYY-MM-DD'
          usageMap[key] = 0;
        }

        // Count lessons
        lessonDocs.forEach((doc) => {
          const data = doc.data();
          const date = data.timestamp?.toDate?.(); // Firestore Timestamp -> JS Date
          if (!date) return;
          const key = date.toISOString().split("T")[0];
          if (usageMap[key] !== undefined) {
            usageMap[key] += 1;
          }
        });

        // Convert to array for chart
        const usageArray = Object.keys(usageMap).map((date) => ({
          date,
          lessons: usageMap[date],
        }));

        setDailyUsage(usageArray);

        setCompleted(userProgress);
      } catch (error) {
        console.error("Error fetching lesson history:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchLessonHistory();
  }, [userId]);

  // Calculate stats
  const completedCount = Object.values(completed).filter(Boolean).length;
  const totalLessonTypes = Object.keys(completed).length;
  const progressPercentage = (completedCount / totalLessonTypes) * 100;
  const allCompleted = progressPercentage === 100;
  //const allCompleted = true;

  // Get the next lesson the user should try
  const getNextLessonToTry = () => {
    for (const lessonType of lessonTypes) {
      if (!completed[lessonType.id]) {
        return lessonType;
      }
    }
    return null;
  };

  // Data for the recharts pie chart
  const chartData = [
    { name: "Completed", value: completedCount, color: "#10b981" },
    {
      name: "Remaining",
      value: totalLessonTypes - completedCount,
      color: "#e2e8f0",
    },
  ];
  if (loading) {
    return (
      <>
        <Box
          sx={{
            display: { xs: "none", md: "block" },
            mb: 1,
          }}
        >
          <Skeleton variant="text" width={300} height={40} sx={{ mt: 4 }} />
        </Box>
        <Paper
          elevation={0}
          sx={{
            ...(styles?.freshBox || {}),
            ...(styles?.softBoxShadow || {}),
            p: { xs: 2, sm: 3 },
            borderRadius: "16px",
            position: "relative",
            overflow: "hidden",
            mt: 1.5,
          }}
        >
          <Box sx={{ mb: 8 }}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              {Array(12)
                .fill(0)
                .map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      width: {
                        xs: "100%",
                        sm: "calc(33% - 8px)",
                        md: "calc(33% - 11px)",
                      },
                      display: "flex",
                      p: 1.2,
                      borderRadius: "12px",
                      alignItems: "center",
                      border: `1px solid #3b82f620`,
                      bgcolor: "background.paper",
                    }}
                  >
                    <Skeleton
                      variant="circular"
                      width={40}
                      height={40}
                      sx={{ mr: 2 }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width="80%" height={24} />
                      <Skeleton variant="text" width="60%" height={20} />
                    </Box>
                  </Box>
                ))}
            </Box>
          </Box>

          <Skeleton
            variant="rectangular"
            height={200}
            sx={{ borderRadius: 3 }}
          />
        </Paper>
      </>
    );
  }
  const nextLesson = getNextLessonToTry();
  const totalLessons = lessonTypes.length;
  const totalCompleted = Object.values(completed).filter(Boolean).length;
  const completionPercentage = Math.round(
    (totalCompleted / totalLessons) * 100,
  );
  return (
    <>
      <Box
        sx={{
          display: { xs: "none", md: "block" },
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Box
          component="h2"
          sx={{
            fontSize: 22,
            fontWeight: "600",
            color: "text.primary",
            m: 0,
            mt: 4,
          }}
        >
          {progressPercentage === 0
            ? `Kom igång med Studera.ai`
            : `Kom igång med Studera.ai (${completionPercentage}%)`}
        </Box>
      </Box>
      <Paper
        elevation={0}
        sx={{
          ...(styles?.freshBox || {}),
          ...(styles?.softBoxShadow || {}),
          p: { xs: 2, sm: 3 },
          borderRadius: "16px",
          position: "relative",
          overflow: "hidden",

          mt: 1.5,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ color: "text.secondary", mb: 3, display: "none" }}
        >
          Utforska funktionerna på Studera.ai
        </Typography>

        <Box sx={{ mb: 8 }}>
          <Box
            sx={{
              //display: "flex",
              display: "none",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1.5,
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, fontSize: "1.2rem" }}
            >
              Alla funktioner
            </Typography>
          </Box>

          {/* Lessons in visual path */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            {lessonTypes
              .sort((a, b) => {
                // First sort by the original group order
                const groupOrderA =
                  lessonGroups.find((g) => g.id === a.group)?.order || 0;
                const groupOrderB =
                  lessonGroups.find((g) => g.id === b.group)?.order || 0;

                if (groupOrderA !== groupOrderB) {
                  return groupOrderA - groupOrderB;
                }

                // Then sort by the item's order within the group
                return a.order - b.order;
              })
              .map((type) => {
                const isCompleted = completed[type.id];

                return (
                  <Box
                    component="button"
                    onClick={() => onLessonSelect(type.id)}
                    sx={{
                      width: {
                        xs: "100%",
                        sm: "calc(33% - 8px)",
                        md: "calc(33% - 11px)",
                      },
                      display: "flex",
                      p: 1.2,
                      borderRadius: "12px",
                      alignItems: "center",
                      border: isCompleted ? "none" : `1px solid ${"#3b82f6"}20`,
                      bgcolor: isCompleted
                        ? `${"#2ba658"}10`
                        : "background.paper",
                      boxShadow: isCompleted ? "" : "0 1px 3px rgba(0,0,0,0.1)",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                      textAlign: "left",
                      "&:focus-visible": {
                        outline: `2px solid ${"#3b82f6"}`,
                        outlineOffset: "2px",
                      },
                      "&:hover": {
                        boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                        transform: "translateY(-2px)",
                        bgcolor: isCompleted
                          ? `${"#2ba658"}15`
                          : `${"#3b82f6"}10`,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        borderRadius: "10px",
                        bgcolor: isCompleted
                          ? `${"#2ba658"}20`
                          : `${"#3b82f6"}15`,
                        p: 1,
                        mr: 2,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {isCompleted ? (
                        <CheckCircleIcon sx={{ color: "#2ba658" }} />
                      ) : (
                        <RadioButtonUncheckedIcon sx={{ color: "#3b82f6" }} />
                      )}
                    </Box>
                    <Box
                      sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 500,
                          color: isCompleted
                            ? "text.secondary"
                            : "text.primary",
                          fontSize: "1rem",
                          textDecoration: isCompleted ? "line-through" : "none",
                        }}
                      >
                        {type.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          display: "none",
                        }}
                      >
                        {isCompleted ? type.benefit : type.description}
                      </Typography>
                    </Box>
                    <ChevronRightIcon
                      sx={{
                        ml: 1,
                        color: isCompleted ? "#2ba658" : "#3b82f6",
                        alignSelf: "center",
                      }}
                    />
                  </Box>
                );
              })}
          </Box>
        </Box>
        {/* Enhanced Overall Progress Header */}
        <Box
          sx={{
            mb: 0,
            p: 3,
            borderRadius: 3,
            bgcolor: "rgba(59, 130, 246, 0.05)",
            border: "1px solid rgba(59, 130, 246, 0.1)",
          }}
        >
          {(() => {
            // Calculate overall completion metrics
            const totalLessons = lessonTypes.length;
            const totalCompleted =
              Object.values(completed).filter(Boolean).length;
            const completionPercentage = Math.round(
              (totalCompleted / totalLessons) * 100,
            );

            // Group completed features by category
            const completedByGroup = {};
            lessonGroups.forEach((group) => {
              const groupLessons = lessonTypes.filter(
                (type) => type.group === group.id,
              );
              const completedInGroup = groupLessons.filter(
                (type) => completed[type.id],
              ).length;
              completedByGroup[group.id] = {
                completed: completedInGroup,
                total: groupLessons.length,
                percentage: Math.round(
                  (completedInGroup / groupLessons.length) * 100,
                ),
              };
            });

            // Calculate recent activity data - last 7 days
            const recentDays = dailyUsage.slice(-7);
            const totalRecentActivity = recentDays.reduce(
              (sum, day) => sum + day.lessons,
              0,
            );
            const mostActiveDay = recentDays.reduce(
              (max, day) => (day.lessons > max.lessons ? day : max),
              { lessons: 0 },
            );

            return (
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  {/* Circle progress indicator */}
                  <Box
                    sx={{ position: "relative", width: 80, height: 80, mr: 3 }}
                  >
                    <svg width="80" height="80" viewBox="0 0 100 100">
                      {/* Background circle */}
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        stroke={
                          theme.palette.mode === "dark" ? "#1d2e3d" : "#e0e0e0"
                        }
                        strokeWidth="10"
                        fill="none"
                      />
                      {/* Progress circle */}
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        stroke={
                          completionPercentage === 100 ? "#2ba658" : "#3b82f6"
                        }
                        strokeWidth="10"
                        fill="none"
                        strokeDasharray={`${2 * Math.PI * 40}`}
                        strokeDashoffset={`${2 * Math.PI * 40 * (1 - completionPercentage / 100)}`}
                        strokeLinecap="round"
                        transform="rotate(-90 50 50)"
                        style={{ transition: "stroke-dashoffset 0.5s ease" }}
                      />
                    </svg>
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: "700",
                          color:
                            completionPercentage === 100
                              ? "#2ba658"
                              : "#3b82f6",
                          lineHeight: 1,
                        }}
                      >
                        {completionPercentage}%
                      </Typography>
                    </Box>
                  </Box>

                  {/* Stats and messaging */}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {completionPercentage === 100
                        ? "Alla Lärstigar Avklarade!"
                        : "Utforskat av Studera.ai"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {totalCompleted} av {totalLessons} funktioner testade
                    </Typography>
                  </Box>
                </Box>

                {/* New: Category breakdown */}

                {/* New: Recent Activity Section */}
                <Box sx={{ mt: 4 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    De senaste 7 dagarna
                  </Typography>

                  <Grid container spacing={2}>
                    {/* Activity Metrics */}
                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: "background.paper",
                          borderRadius: 2,
                          border: "1px solid rgba(0,0,0,0.05)",
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                        }}
                      >
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Totalt antal genereringar
                          </Typography>
                          <Typography
                            variant="h4"
                            sx={{ fontWeight: 700, my: 1 }}
                          >
                            {totalRecentActivity}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Mest aktiva dagen
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "baseline" }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {mostActiveDay.lessons > 0
                                ? mostActiveDay.lessons
                                : "-"}{" "}
                              genereringar
                            </Typography>
                            {mostActiveDay.lessons > 0 && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ ml: 1 }}
                              >
                                {new Date(
                                  mostActiveDay.date,
                                ).toLocaleDateString("sv-SE", {
                                  weekday: "long",
                                })}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </Grid>

                    {/* Replace Mini Bar Chart with this Area Chart */}
                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: "background.paper",
                          borderRadius: 2,
                          border: "1px solid rgba(0,0,0,0.05)",
                          height: "100%",
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          Genereringar per dag
                        </Typography>

                        <Box sx={{ height: 100, width: "100%" }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <AreaChart
                              data={recentDays}
                              margin={{ top: 5, right: 0, left: 0, bottom: 5 }}
                            >
                              <defs>
                                <linearGradient
                                  id="colorLessons"
                                  x1="0"
                                  y1="0"
                                  x2="0"
                                  y2="1"
                                >
                                  <stop
                                    offset="5%"
                                    stopColor="#3b82f6"
                                    stopOpacity={0.3}
                                  />
                                  <stop
                                    offset="95%"
                                    stopColor="#3b82f6"
                                    stopOpacity={0}
                                  />
                                </linearGradient>
                              </defs>
                              <Area
                                type="monotone"
                                dataKey="lessons"
                                stroke="#3b82f6"
                                strokeWidth={1}
                                fillOpacity={1}
                                fill="url(#colorLessons)"
                              />
                              <XAxis
                                dataKey="date"
                                axisLine={false}
                                tickLine={false}
                                tick={{
                                  fontSize: 10,
                                  fill: "#94a3b8",
                                }}
                                tickFormatter={(date) =>
                                  new Date(date)
                                    .toLocaleDateString("sv-SE", {
                                      weekday: "short",
                                    })
                                    .substring(0, 2)
                                }
                              />
                              <Tooltip
                                formatter={(value) => [
                                  `${value} genereringar`,
                                  "Genereringar",
                                ]}
                                labelFormatter={(label) =>
                                  new Date(label).toLocaleDateString("sv-SE", {
                                    weekday: "long",
                                    month: "short",
                                    day: "numeric",
                                  })
                                }
                                contentStyle={{
                                  borderRadius: 8,
                                  border: "none",
                                  boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                                  padding: "8px 12px",
                                  fontSize: 12,
                                }}
                              />
                            </AreaChart>
                          </ResponsiveContainer>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                {/* New: Next Goal */}
                {!allCompleted && nextLesson && (
                  <Box
                    sx={{
                      mt: 3,
                      p: 2,
                      bgcolor: "#f0f7ff",
                      borderRadius: 2,
                      border: "1px dashed #3b82f6",
                      display: "none",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <LightbulbIcon sx={{ color: "#3b82f6", mr: 1 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Nästa utmaning
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mt: 1,
                        cursor: "pointer",
                        "&:hover": {
                          "& .next-arrow": {
                            transform: "translateX(3px)",
                          },
                        },
                      }}
                      onClick={() => onLessonSelect(nextLesson.id)}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          flex: 1,
                        }}
                      >
                        <Typography variant="body2">
                          {nextLesson.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {nextLesson.description}
                        </Typography>
                      </Box>
                      <ArrowForwardIcon
                        className="next-arrow"
                        sx={{
                          color: "#3b82f6",
                          transition: "transform 0.2s ease",
                        }}
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })()}
        </Box>
        {/* Reorganized Structure: Chart + Next Lesson side-by-side */}
        <Grid container spacing={3} sx={{ mb: 2, mt: 3, display: "none" }}>
          {/* Progress Chart in Left Column */}
          <Grid item xs={12} md={6}>
            <Box sx={{ height: 120 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={chartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={32}
                        outerRadius={52}
                        paddingAngle={5}
                        dataKey="value"
                        startAngle={90}
                        endAngle={-270}
                      >
                        {chartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value) => [
                          `${value} av ${totalLessonTypes}`,
                          "Funktioner",
                        ]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </Grid>

                <Grid item xs={12} sm={8}>
                  <Box sx={{ pt: 2 }}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        color: allCompleted ? "#10b981" : "#3b82f6",
                        mb: 1,
                      }}
                    >
                      {progressPercentage.toFixed(0)}%
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 500, mb: 4, color: "text.secondary" }}
                    >
                      {allCompleted
                        ? "Du har utforskat alla funktioner!"
                        : `Du har testat ${completedCount} av ${totalLessonTypes} funktioner`}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Grid>

          {/* Next Lesson Spotlight in Right Column - REPLACED THE AREA CHART WITH NEXT LESSON COMPONENT */}
          {!allCompleted && nextLesson && (
            <Grid item xs={12} md={6}>
              <Card
                elevation={0}
                sx={{
                  border: `1px dashed ${nextLesson.color}20`,
                  bgcolor: `${nextLesson.color}10`,
                  borderRadius: "12px",
                  height: "100%",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                    borderColor: `${nextLesson.color}40`,
                    "& .next-lesson-arrow": {
                      transform: "translateX(4px)",
                    },
                  },
                  cursor: "pointer",
                }}
                onClick={() => onLessonSelect(nextLesson.id)}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: `${nextLesson.color}20`,
                        borderRadius: "8px",
                        p: 1,
                        mr: 2,
                      }}
                    >
                      {React.createElement(nextLesson.icon, {
                        sx: { color: nextLesson.color },
                      })}
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, flex: 1, fontSize: "1.1rem" }}
                    >
                      Prova nästa funktion: {nextLesson.name}
                    </Typography>
                  </Box>

                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 500,
                      mb: 1,
                      mt: 2,
                      color: "text.primary",
                    }}
                  ></Typography>

                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", mb: 0 }}
                  >
                    {nextLesson.description || nextLesson.benefit}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Paper>
      {/* Add this at the end of your component's return statement */}
      {/* Dialog component with video on the left and text on the right */}
      {selectedLesson && (
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="xl"
          PaperProps={{
            sx: {
              borderRadius: 2,
              p: 2,
              width: "100%",
              maxWidth: 1100,
            },
          }}
        >
          <DialogTitle>
            <Typography
              variant="h4"
              component="h1"
              sx={{ fontWeight: "bold", mb: 5 }}
            >
              {selectedLesson.dialogContent?.title || selectedLesson.name}
            </Typography>
            <IconButton
              aria-label="close"
              onClick={() => setDialogOpen(false)}
              sx={{
                position: "absolute",
                right: 16,
                top: 16,
                color: "text.secondary",
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent>
            <Grid container spacing={5}>
              {/* Content on the right */}
              <Grid item xs={12} md={6}>
                {/* List Items Section */}
                {selectedLesson.dialogContent?.listItems && (
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 500, mb: 2, fontSize: "1.1rem" }}
                    >
                      Så här gör du:
                    </Typography>
                    {selectedLesson.dialogContent.listItems.map(
                      (listItem, index) => (
                        <InstructionItem
                          key={index}
                          text={listItem.text}
                          marginBottom={1}
                          simple="true"
                          fontWeight="400"
                          color="text.secondary"
                        />
                      ),
                    )}
                  </Box>
                )}

                <DialogContentText align="left">
                  {selectedLesson.dialogContent?.description}
                </DialogContentText>
                <Divider />
                <DialogContentText align="left" sx={{ mt: 2 }}>
                  Glöm inte att skapa ett elevrum och bjud in dina elever.
                  Annars går det inte att dela innehållet.
                </DialogContentText>
              </Grid>
              {/* Video on the left */}
              <Grid item xs={12} md={6}>
                {selectedLesson.dialogContent?.videoUrl && (
                  <VideoPlayer
                    videoUrl={selectedLesson.dialogContent.videoUrl}
                    poster={selectedLesson.dialogContent.posterUrl}
                    autoPlay={true}
                  />
                )}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ mt: 6 }}>
            <Button
              variant="print"
              onClick={() => {
                setDialogOpen(false);
                navigate("/app/rum");
              }}
            >
              Skapa elevrum
            </Button>
            <Button
              variant="purp"
              onClick={() => {
                setDialogOpen(false);
                navigate(
                  selectedLesson.dialogContent?.createPath || "/dashboard",
                );
              }}
            >
              {selectedLesson.dialogContent?.createButtonText ||
                "Skapa innehåll"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default ExplorationProgress;
